import { useState, useEffect, useRef } from 'react';
import SidePanel from '../ui/SidePanel';
import Input from '../ui/Input';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import Popover from '../ui/Popover';
import RecipientsField from './RecipientsField';
import { supabase } from '../../lib/supabase';
import { MonitoredSite } from '../../lib/uptime';

interface SiteSidepanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSiteChanged: () => void;
  site?: MonitoredSite | null; // null/undefined = add mode, populated = edit mode
}

const SNOOZE_HOURS = [1, 2, 4, 8];

export default function SiteSidepanel({ isOpen, onClose, onSiteChanged, site }: SiteSidepanelProps) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [recipients, setRecipients] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSnoozeOptions, setShowSnoozeOptions] = useState(false);
  const [isSnoozingAction, setIsSnoozingAction] = useState(false);
  const snoozeRef = useRef<HTMLDivElement>(null);

  const isEditMode = !!site;

  // Populate form when site changes
  useEffect(() => {
    if (site) {
      setName(site.name || '');
      setUrl(site.url || '');
      setRecipients(site.alert_recipients || []);
    } else {
      setName('');
      setUrl('');
      setRecipients([]);
    }
    setError(null);
    setShowDeleteConfirm(false);
    setShowSnoozeOptions(false);
  }, [site, isOpen]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!url.trim()) {
      setError('URL is required');
      return;
    }

    let finalUrl = url.trim();
    if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
      finalUrl = 'https://' + finalUrl;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode && site) {
        const { error: updateError } = await supabase
          .from('monitored_sites')
          .update({
            name: name.trim(),
            url: finalUrl,
            alert_recipients: recipients,
          })
          .eq('id', site.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('monitored_sites')
          .insert({
            name: name.trim(),
            url: finalUrl,
            is_active: true,
            alert_recipients: recipients,
          });

        if (insertError) throw insertError;
      }

      onSiteChanged();
      handleClose();
    } catch (err) {
      console.error('Error saving site:', err);
      setError(err instanceof Error ? err.message : 'Failed to save site');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!site) return;
    setIsDeleting(true);
    setError(null);

    try {
      // Delete uptime checks first (FK constraint)
      const { error: checksError } = await supabase
        .from('uptime_checks')
        .delete()
        .eq('site_id', site.id);

      if (checksError) throw checksError;

      // Delete incidents
      await supabase
        .from('uptime_incidents')
        .delete()
        .eq('site_id', site.id);

      // Delete the site
      const { error: deleteError } = await supabase
        .from('monitored_sites')
        .delete()
        .eq('id', site.id);

      if (deleteError) throw deleteError;

      onSiteChanged();
      handleClose();
    } catch (err) {
      console.error('Error deleting site:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete site');
    } finally {
      setIsDeleting(false);
    }
  };

  const siteIsSnoozed = site?.alerts_snoozed_until && new Date(site.alerts_snoozed_until) > new Date();

  const formatSnoozeExpiry = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const handleSnooze = async (hours: number) => {
    if (!site) return;
    setIsSnoozingAction(true);
    const until = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
    const { error: err } = await supabase
      .from('monitored_sites')
      .update({ alerts_snoozed_until: until })
      .eq('id', site.id);
    if (!err) onSiteChanged();
    setIsSnoozingAction(false);
    setShowSnoozeOptions(false);
  };

  const handleUnsnooze = async () => {
    if (!site) return;
    setIsSnoozingAction(true);
    const { error: err } = await supabase
      .from('monitored_sites')
      .update({ alerts_snoozed_until: null })
      .eq('id', site.id);
    if (!err) onSiteChanged();
    setIsSnoozingAction(false);
  };

  const handleClose = () => {
    setName('');
    setUrl('');
    setError(null);
    setShowDeleteConfirm(false);
    setShowSnoozeOptions(false);
    onClose();
  };

  return (
    <SidePanel
      open={isOpen}
      onClose={handleClose}
      eyebrow="Monitor"
      title={isEditMode ? 'Edit Site' : 'Add Site'}
      width={420}
      footer={
        <>
          <Button
            icon={isEditMode ? 'check' : 'plus'}
            type="submit"
            form="ck-site-form"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? isEditMode ? 'Saving...' : 'Adding...'
              : isEditMode ? 'Save Changes' : 'Add Site'}
          </Button>
          <Button variant="ghost" type="button" onClick={handleClose}>
            Cancel
          </Button>
        </>
      }
    >
      <form
        id="ck-site-form"
        onSubmit={handleSubmit}
        className="ck-stagger"
        style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}
      >
        {error && (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--status-down-soft)',
              border: '1px solid rgba(229, 72, 77, 0.25)',
              color: 'var(--status-down)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {error}
          </div>
        )}

        <Input
          label="URL"
          icon="globe"
          placeholder="example.com"
          value={url}
          onChange={e => setUrl(e.target.value)}
        />

        <Input
          label="Display Name"
          icon="pencil"
          placeholder="e.g. My Client Site"
          value={name}
          onChange={e => setName(e.target.value)}
        />

        <RecipientsField value={recipients} onChange={setRecipients} />

        <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          https:// is added automatically if not provided.
        </p>

        {/* Alert Snooze - Edit mode only */}
        {isEditMode && (
          <div
            style={{
              paddingTop: '18px',
              borderTop: '1px solid var(--border-hairline)',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <span className="t-eyebrow">Alert Snooze</span>

            {siteIsSnoozed ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '10px',
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--status-warn-soft)',
                  border: '1px solid rgba(217, 160, 60, 0.25)',
                }}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--status-warn)',
                  }}
                >
                  <Icon name="bell-off" size={14} />
                  Snoozed until {formatSnoozeExpiry(site!.alerts_snoozed_until!)}
                </span>
                <button
                  type="button"
                  onClick={handleUnsnooze}
                  disabled={isSnoozingAction}
                  className="hover-wash pressable"
                  style={{
                    border: 'none',
                    background: 'transparent',
                    borderRadius: 'var(--radius-sm)',
                    padding: '5px 9px',
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--status-warn)',
                    cursor: isSnoozingAction ? 'not-allowed' : 'pointer',
                    opacity: isSnoozingAction ? 0.5 : 1,
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div ref={snoozeRef} style={{ position: 'relative' }}>
                <Button
                  type="button"
                  variant="secondary"
                  icon="bell-off"
                  disabled={isSnoozingAction}
                  onClick={() => setShowSnoozeOptions(prev => !prev)}
                  style={{ width: '100%' }}
                >
                  Snooze Alerts
                </Button>

                <Popover
                  open={showSnoozeOptions}
                  anchorEl={snoozeRef.current}
                  onClose={() => setShowSnoozeOptions(false)}
                  matchWidth
                  role="menu"
                >
                    <div
                      className="t-eyebrow"
                      style={{
                        padding: '7px 10px 5px',
                        borderBottom: '1px solid var(--border-hairline)',
                        marginBottom: '3px',
                      }}
                    >
                      Snooze alerts for...
                    </div>
                    {SNOOZE_HOURS.map(h => (
                      <button
                        key={h}
                        type="button"
                        className="hover-wash pressable"
                        onClick={() => handleSnooze(h)}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          padding: '7px 10px',
                          border: 'none',
                          background: 'transparent',
                          borderRadius: 'var(--radius-sm)',
                          cursor: 'pointer',
                          fontFamily: 'var(--font-ui)',
                          fontSize: 'var(--text-sm)',
                          color: 'var(--text-body)',
                        }}
                      >
                        {h} hour{h > 1 ? 's' : ''}
                      </button>
                    ))}
                </Popover>
              </div>
            )}
          </div>
        )}

        {/* Delete Section - Edit mode only */}
        {isEditMode && (
          <div
            style={{
              paddingTop: '18px',
              borderTop: '1px solid var(--border-hairline)',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <span className="t-eyebrow">Danger Zone</span>

            {!showDeleteConfirm ? (
              <Button
                type="button"
                variant="danger"
                icon="trash-2"
                onClick={() => setShowDeleteConfirm(true)}
                style={{ width: '100%' }}
              >
                Delete Site
              </Button>
            ) : (
              <div
                style={{
                  padding: '14px',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--status-down-soft)',
                  border: '1px solid rgba(229, 72, 77, 0.25)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px',
                    fontWeight: 500,
                    color: 'var(--status-down)',
                    fontSize: 'var(--text-base)',
                  }}
                >
                  <Icon name="circle-alert" size={15} />
                  Are you sure?
                </div>
                <p
                  style={{
                    margin: '0 0 12px',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-muted)',
                  }}
                >
                  This permanently deletes <strong style={{ color: 'var(--text-body)' }}>{site?.name}</strong> and
                  all its uptime history. This cannot be undone.
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    icon="trash-2"
                    disabled={isDeleting}
                    onClick={handleDelete}
                    style={{ flex: 1 }}
                  >
                    {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(false)}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </form>
    </SidePanel>
  );
}
